// @mui
import {
  Box,
  Card,
  Container,
  Grid,
  Paper,
  Table,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Slide,
  TableBody,
  Pagination,
  Button,
} from '@mui/material';
import { TableHeadCustom, TableNoData } from 'components/table';
import { useTranslation } from 'react-i18next';
import Page from '../../../components/Page';
import onFail from 'assets/images/onfail.png';
import Image from 'components/Image';
import syncStage from 'assets/images/sync.png';
import processComplete from 'assets/images/processComplete.png';
import { LoadingButton } from '@mui/lab';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useState } from 'react';
import { requestgetAllProducts, getProductsCount } from 'api/ShoplineApiHooks';
import PartialPageSpinner from 'components/PartialPageSpinner';
import Scrollbar from 'components/Scrollbar';
import {
  ProductsCountType,
  ProductsList,
  ProductsListType,
} from 'pages/ShoplineType';
import { useQuery } from 'react-query';
import {
  GET_ALL_PRODUCTS_API_URL,
  GET_PRODUCTS_COUNT_API_URL,
  ROWS_PER_PAGE,
  UPLOAD_MANY_PRODUCT_API_URL,
} from 'api/ShoplineApiConstants';
import useTable from 'hooks/useTable';
import ProductTableToolbar from './child/ProductTableToolbar';
import ProductsTableRow from './child/ProductsTableRow';
import { useSnackbar } from 'notistack';
import useConnector from 'hooks/useConnector';
import axiosInstance from 'utils/axios';
import useAuth from 'hooks/useAuth';
import { capitalizeFirstLetter } from 'utils/letterChange';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export default function Products() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { sourceShopId, targetShopId } = useConnector();
  const [openSync, setOpenSync] = React.useState(false);
  const [openComplete, setopenComplete] = React.useState(false);
  const [openError, setError] = React.useState(false);
  const [filterName, setFilterName] = useState('');
  const [filter, setFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const TABLE_HEAD = [
    { id: 'product', label: t('products'), align: 'center' },
    { id: 'product', label: t('product_id'), align: 'center' },
    { id: 'price', label: t('price'), align: 'center' },
    { id: 'status', label: t('status'), align: 'center' },
    { id: 'view_in_hs', label: t('view_in_hubspot'), align: 'center' },
    { id: 'action', label: t('action'), align: 'right' },
  ];

  let contentLoad: React.ReactNode = null;
  const handleClose = () => {
    setOpenSync(false);
    setError(false);
    setopenComplete(false);
  };

  const { selected, onSelectRow, onSelectAllRows } = useTable({
    defaultOrderBy: 'first_name',
  });

  const activePage = skip / ROWS_PER_PAGE + 1;

  const { data, isLoading } = useQuery<ProductsListType>(
    [GET_ALL_PRODUCTS_API_URL, skip, filterName],
    () =>
      requestgetAllProducts(
        sourceShopId,
        targetShopId,
        ROWS_PER_PAGE,
        activePage,
        filterName
      )
  );

  const { data: productsCountData, isLoading: isLoadingCount } =
    useQuery<ProductsCountType>([GET_PRODUCTS_COUNT_API_URL], () =>
      getProductsCount(sourceShopId, targetShopId)
    );

  const totalPage = productsCountData?.data?.count
    ? Math.ceil((productsCountData?.data?.count ?? 0) / ROWS_PER_PAGE)
    : 0;

  const exportBulk = async () => {
    if (selected.length) {
      setOpenSync(true);
      try {
        const response = await axiosInstance.post(
          UPLOAD_MANY_PRODUCT_API_URL,
          {
            source: {
              marketplace: 'shopline',
              shopId: sourceShopId,
            },
            target: {
              marketplace: 'hubspot',
              shopId: targetShopId,
            },
            limit: 10,
            source_product_ids: selected,
            user_id: (user?.user_id || '') as string,
          },
          {
            headers: {
              'Ced-Source-Id': sourceShopId,
              'Ced-Target-Id': targetShopId,
            },
          }
        );
        if (response.data.success) {
          enqueueSnackbar(capitalizeFirstLetter(response.data.message), {
            variant: 'success',
          });
          setopenComplete(true);
          setOpenSync(false);
        } else {
          setOpenSync(false);
          setError(true);
          enqueueSnackbar(capitalizeFirstLetter(response.data.message), {
            variant: 'error',
          });
        }
      } catch (error) {}
    } else {
      enqueueSnackbar(
        capitalizeFirstLetter(t('select_atleast_one_product_to_export')),
        {
          variant: 'error',
        }
      );
    }
  };

  const mapPages = (data: ProductsList[]) => {
    const rows: React.ReactNode[] = [];
    data.forEach(row => {
      rows.push(
        <ProductsTableRow
          key={row.source_product_id}
          row={row}
          selected={selected.includes(row.source_product_id)}
          onSelectRow={() => onSelectRow(row.source_product_id)}
          activePage={activePage}
          filterName={filterName}
          skip={skip}
        />
      );
    });

    return rows;
  };

  const handleFilterName = (filterName: string) => {
    setFilter(filterName);
  };
  useEffect(() => {
    if (filter === '' || filter.length >= 3) {
      setFilterName(filter);
      setSkip(0);
    }
  }, [filter]);

  const products = data?.data;
  if (products) {
    contentLoad = products?.rows.length ? (
      <TableBody>{mapPages(products?.rows)}</TableBody>
    ) : (
      <>
        <TableNoData isNotFound={true} Message={t('products')} />
      </>
    );
  }

  return (
    <Page title={t('products')}>
      {/* <Container maxWidth='xl' sx={{ padding: '5px' }}> */}
      {/* <Box sx={{ flexGrow: 1 }} /> */}
      <Card sx={{ mx: 4 }}>
        <Grid container sx={{ p: 1 }} direction='row'>
          <ProductTableToolbar
            filterName={filter}
            onFilterName={handleFilterName}
            placeholder={t('search_product')}
          />
          <LoadingButton
            sx={{
              position: 'absolute',
              top: '35px',
              right: '20px',
            }}
            variant='contained'
            loading={false}
            onClick={exportBulk}
          >
            {t('sync_selected_data_row')}
          </LoadingButton>
        </Grid>
        {isLoading || isLoadingCount ? (
          <PartialPageSpinner />
        ) : (
          <>
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 800 }} aria-label='simple table'>
                  <TableHeadCustom
                    rowCount={ROWS_PER_PAGE}
                    numSelected={selected.length}
                    headLabel={TABLE_HEAD}
                    onSelectAllRows={checked =>
                      onSelectAllRows(
                        checked,
                        products?.rows
                          ? products?.rows.map(row => row.source_product_id)
                          : []
                      )
                    }
                  />
                  {contentLoad}
                </Table>
              </TableContainer>
            </Scrollbar>
            <Card
              sx={{
                display: 'flex',
                paddingBottom: '25px',
                justifyContent: 'center',
                paddingTop: '10px',
              }}
            >
              <Pagination
                color='primary'
                variant='outlined'
                count={totalPage}
                page={Math.ceil(skip / ROWS_PER_PAGE + 1)}
                onChange={(e, pageNo) => setSkip((pageNo - 1) * ROWS_PER_PAGE)}
              />
            </Card>
          </>
        )}
        <Dialog
          open={openSync}
          TransitionComponent={Transition}
          keepMounted
          aria-describedby='alert-dialog-slide-description'
        >
          <Box
            noValidate
            component='form'
            sx={{
              textAlign: 'center',
            }}
          >
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  m: 'auto',
                  width: 'fit-content',
                }}
              >
                <DialogContentText id='alert-dialog-slide-description'>
                  <Image
                    src={syncStage}
                    visibleByDefault
                    disabledEffect
                    sx={{
                      objectFit: 'contain',
                      width: '120px',
                      height: '120px',
                    }}
                    alt='success'
                  />
                </DialogContentText>
              </Box>
              <DialogTitle>{t('sync_in_progress')}</DialogTitle>
              <DialogContentText sx={{ justifyContent: 'center' }}>
                {t('you_can_see_your_sync_progress_in_the_activity_tab')}
              </DialogContentText>
            </DialogContent>
          </Box>
        </Dialog>
        <Dialog
          open={openComplete}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleClose}
          aria-describedby='alert-dialog-slide-description'
        >
          <Box
            noValidate
            component='form'
            sx={{
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  m: 'auto',
                  width: 'fit-content',
                }}
              >
                <DialogContentText id='alert-dialog-slide-description'>
                  <Image
                    src={processComplete}
                    visibleByDefault
                    disabledEffect
                    sx={{
                      objectFit: 'contain',
                      width: '120px',
                      height: '120px',
                    }}
                    alt='success'
                  />
                </DialogContentText>
              </Box>
              <DialogTitle>{t('sync_successfull')}</DialogTitle>
              <DialogContentText sx={{ justifyContent: 'center' }}>
                {t('all_the_data_is_successfully_synced')}
              </DialogContentText>
              <DialogContentText sx={{ justifyContent: 'center' }}>
                {t('close_this_window_to_see_updated_data_tables')}
              </DialogContentText>
            </DialogContent>
          </Box>
          <DialogActions>
            <Button onClick={handleClose} autoFocus>
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openError}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleClose}
          aria-describedby='alert-dialog-slide-description'
        >
          <Box
            noValidate
            component='form'
            sx={{
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  m: 'auto',
                  width: 'fit-content',
                }}
              >
                <DialogContentText id='alert-dialog-slide-description'>
                  <Image
                    src={onFail}
                    visibleByDefault
                    disabledEffect
                    sx={{
                      objectFit: 'contain',
                      width: '120px',
                      height: '120px',
                    }}
                    alt='success'
                  />
                </DialogContentText>
              </Box>
              <DialogTitle>{t('sync_unsuccessfull')}</DialogTitle>
              <DialogContentText sx={{ justifyContent: 'center' }}>
                {t('unable_to_sync_all_the_data')}
              </DialogContentText>
              <DialogContentText sx={{ justifyContent: 'center' }}>
                {t('close_this_window_to_see_updated_data_tables')}
              </DialogContentText>
            </DialogContent>
          </Box>
          <DialogActions>
            <Button onClick={handleClose} autoFocus>
              {t('close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
      {/* </Container> */}
    </Page>
  );
}
